import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams } from "react-router-dom";

// Convert every image matched by Webpack's require.context into an array.
function importAll(context) {
  return context.keys().map(context);
}

// Associate each URL category with the local folder that contains its images.
const galleries = {
  graduations: importAll(
    require.context("./images/graduations", false, /\.(jpg|jpeg|png)$/)
  ),
  portraits: importAll(
    require.context("./images/portraits", false, /\.(jpg|jpeg|png)$/)
  ),
  products: importAll(
    require.context("./images/products", false, /\.(jpg|jpeg|png)$/)
  ),
  weddings: importAll(
    require.context("./images/weddings", false, /\.(jpg|jpeg|png)$/)
  ),
  headshots: importAll(
    require.context("./images/headshots", false, /\.(jpg|jpeg|png)$/)
  ),
};

function Gallery() {
  const { category } = useParams();
  const normalizedCategory = category ? category.toLowerCase() : "";

  const images = useMemo(() => {
    const imagesRaw = galleries[normalizedCategory] || [];
    return imagesRaw.map((src) => src.replace(/^\./, ""));
  }, [normalizedCategory]);

  const [index, setIndex] = useState(0);
  const [maximizedIndex, setMaximizedIndex] = useState(null);

  const wheelTimeoutRef = useRef(null);
  const swipeStartRef = useRef(null);
  const didSwipeRef = useRef(false);

  const goNext = useCallback(() => {
    if (!images.length) return;

    setIndex((previousIndex) => (previousIndex + 1) % images.length);
  }, [images.length]);

  const goPrev = useCallback(() => {
    if (!images.length) return;

    setIndex(
      (previousIndex) => (previousIndex - 1 + images.length) % images.length
    );
  }, [images.length]);

  useEffect(() => {
    if (!images.length) return;

    setIndex((previousIndex) =>
      previousIndex >= images.length ? 0 : previousIndex
    );
  }, [images.length]);

  const handlePointerDown = useCallback((event) => {
    if (event.pointerType === "mouse") return;

    didSwipeRef.current = false;
    swipeStartRef.current = {
      x: event.clientX,
      y: event.clientY,
    };
  }, []);

  const handlePointerUp = useCallback(
    (event) => {
      if (event.pointerType === "mouse" || !swipeStartRef.current) return;

      const { x: startX, y: startY } = swipeStartRef.current;
      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      const swipeThreshold = 50;

      swipeStartRef.current = null;

      if (
        Math.abs(deltaX) < swipeThreshold ||
        Math.abs(deltaX) <= Math.abs(deltaY)
      ) {
        return;
      }

      didSwipeRef.current = true;

      if (deltaX < 0) {
        goNext();
      } else {
        goPrev();
      }
    },
    [goNext, goPrev]
  );

  const handlePointerCancel = useCallback(() => {
    swipeStartRef.current = null;
    didSwipeRef.current = false;
  }, []);

  const handleZoneClick = useCallback(
    (direction) => {
      if (didSwipeRef.current) {
        didSwipeRef.current = false;
        return;
      }

      if (direction === "previous") {
        goPrev();
      } else {
        goNext();
      }
    },
    [goNext, goPrev]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (!images.length) return;

      if (maximizedIndex !== null) {
        if (event.key === "ArrowRight") {
          event.preventDefault();

          setMaximizedIndex(
            (previousIndex) => (previousIndex + 1) % images.length
          );
        } else if (event.key === "ArrowLeft") {
          event.preventDefault();

          setMaximizedIndex(
            (previousIndex) =>
              (previousIndex - 1 + images.length) % images.length
          );
        } else if (event.key === "Escape") {
          setMaximizedIndex(null);
        }

        return;
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        goNext();
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goPrev();
      }
    },
    [goNext, goPrev, images.length, maximizedIndex]
  );

  const handleWheel = useCallback(
    (event) => {
      if (maximizedIndex === null || !images.length) return;
      if (wheelTimeoutRef.current) return;

      wheelTimeoutRef.current = setTimeout(() => {
        wheelTimeoutRef.current = null;
      }, 400);

      if (event.deltaY > 0) {
        setMaximizedIndex(
          (previousIndex) => (previousIndex + 1) % images.length
        );
      }

      if (event.deltaY < 0) {
        setMaximizedIndex(
          (previousIndex) =>
            (previousIndex - 1 + images.length) % images.length
        );
      }
    },
    [images.length, maximizedIndex]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    if (maximizedIndex !== null) {
      window.addEventListener("wheel", handleWheel, { passive: true });
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("wheel", handleWheel);

      if (wheelTimeoutRef.current) {
        clearTimeout(wheelTimeoutRef.current);
        wheelTimeoutRef.current = null;
      }
    };
  }, [handleKeyDown, handleWheel, maximizedIndex]);

  if (!images.length) {
    return (
      <section className="flex min-h-screen items-center justify-center px-4 pb-16 pt-28 sm:px-6 sm:pt-32">
        <div className="text-center text-gray-500">
          <h2 className="mb-4 text-5xl font-light leading-none tracking-widest text-gray-300">
            EMPTY
          </h2>

          <p className="text-xl">
            No images found for{" "}
            <strong>{normalizedCategory || category}</strong>
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="relative px-3 pb-16 pt-28 sm:px-6 sm:pt-32 md:pt-36">
      <div className="mx-auto w-full max-w-7xl">
        <div className="flex flex-col items-center">
          <div
            className="relative mx-auto flex h-[calc(100dvh-15rem)] min-h-[420px] w-full max-w-[1400px] touch-pan-y items-center justify-center overflow-hidden rounded-2xl bg-white sm:h-[calc(100dvh-17rem)]"
            tabIndex={0}
            role="region"
            aria-roledescription="carousel"
            aria-label={`${normalizedCategory} gallery image ${index + 1} of ${images.length}`}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
          >
            <img
              src={images[index]}
              alt={`${normalizedCategory} ${index + 1} of ${images.length}`}
              className="h-full w-full select-none rounded-xl object-contain"
              loading="lazy"
              draggable="false"
            />

            <button
              type="button"
              onClick={() => handleZoneClick("previous")}
              className="absolute inset-y-0 left-0 z-20 w-1/2 cursor-w-resize outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white"
              aria-label="Previous image"
            >
              <span className="sr-only">Previous image</span>
            </button>

            <button
              type="button"
              onClick={() => handleZoneClick("next")}
              className="absolute inset-y-0 right-0 z-20 w-1/2 cursor-e-resize outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white"
              aria-label="Next image"
            >
              <span className="sr-only">Next image</span>
            </button>

            <button
              type="button"
              onClick={() => setMaximizedIndex(index)}
              className="absolute bottom-3 left-1/2 z-30 -translate-x-1/2 rounded-full bg-black/45 px-3 py-1 text-sm font-medium text-white transition hover:bg-black/60 focus:outline-none focus:ring-2 focus:ring-white"
              aria-label={`Open image ${index + 1} full screen`}
            >
              {index + 1} / {images.length}
            </button>
          </div>
        </div>
      </div>

      {maximizedIndex !== null && (
        <div
          className="fixed inset-x-0 bottom-0 top-[72px] z-40 flex items-center justify-center bg-black/90 p-4 pt-6 sm:top-0 sm:z-50 sm:p-8"
          role="dialog"
          aria-modal="true"
          aria-label={`${normalizedCategory} image ${maximizedIndex + 1} of ${images.length}`}
          onClick={() => setMaximizedIndex(null)}
        >
          <img
            src={images[maximizedIndex]}
            alt={`${normalizedCategory} ${maximizedIndex + 1} of ${images.length}`}
            className="max-h-full max-w-full object-contain"
            onClick={(event) => event.stopPropagation()}
          />

          <button
            type="button"
            onClick={() => setMaximizedIndex(null)}
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-2xl text-gray-900 transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-white sm:right-6 sm:top-6 sm:h-11 sm:w-11"
            aria-label="Close full screen image"
          >
            ×
          </button>
        </div>
      )}
    </section>
  );
}

export default Gallery;